import { Component, inject, WritableSignal, signal, computed } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil, take } from "rxjs";
import { UserPlanType, PlanTemplateDto, InvoiceDto } from "../../../../api/models/fraud.models";
import { dialogRiseAnimation, purchaseStageSwitchAnimation } from "../../../animations/pricing-animations";
import { PlanTemplatesService } from "../../../services/plan-templates.service";
import { VariablesService } from "../../../services/variables.service";
import { RenewalDialogClosePayload } from "../dialog-renewal/dialog-renewal.component";
import { InvoicePlanUpgradeComponent } from "../invoices/invoice-plan-upgrade/invoice-plan-upgrade.component";
import { PurchaseFlowStageChooseBillingComponent } from "./components/purchase-flow-stage-choose-billing/purchase-flow-stage-choose-billing.component";
import { AuthService } from "../../../services/auth.service";
import { ApiService } from "../../../../api/services/api.service";
import { ProjectSettingsService } from "../../../services/project-settings.service";
import { TranslateModule } from "@ngx-translate/core";


export type BillingType = 'monthly' | 'annual';
export type PurchaseStage = 'choose_billing_state' | 'invoice_state' | 'payment_received_state' | 'support_error_state';
/** Backend error code: plan invoice could not be generated, user has to contact support. */
const ERROR_CODE_PLAN_INVOICE_UNAVAILABLE = 86;

export type UsdtNetwork = 'TRC-20' | 'ERC-20';

export interface PurchaseFlowData {
  tier: UserPlanType;
  initialStage?: PurchaseStage;
  initialBilling?: BillingType;
  invoiceId?: string;
  trc20Address?: string;
  erc20Address?: string;
}

@Component({
  selector: 'app-dialog-purchase-flow',
  imports: [
    PurchaseFlowStageChooseBillingComponent,
    InvoicePlanUpgradeComponent,
    TranslateModule
  ],
  templateUrl: './dialog-purchase-flow.component.html',
  styleUrl: './dialog-purchase-flow.component.scss',
  animations: [dialogRiseAnimation, purchaseStageSwitchAnimation]
})
export class DialogPurchaseFlowComponent {
  public readonly data: PlanTemplateDto = inject(MAT_DIALOG_DATA, { optional: true });

  public readonly authService: AuthService = inject(AuthService);
  public readonly _apiService: ApiService = inject(ApiService);
  private readonly _planTemplatesService = inject(PlanTemplatesService);
  readonly dialogRef = inject(MatDialogRef<DialogPurchaseFlowComponent>);
  readonly variablesService = inject(VariablesService);
  private readonly _projectSettingsService = inject(ProjectSettingsService);

  public readonly stage: WritableSignal<PurchaseStage> = signal<PurchaseStage>('choose_billing_state');
  public readonly billing: WritableSignal<BillingType> = signal<BillingType>('annual');
  public readonly planTemplates = this._planTemplatesService.planTemplates;
  public readonly invoiceUuid: WritableSignal<string> = signal<string>('');
  public readonly invoiceStatus: WritableSignal<InvoiceDto['status']> = signal<InvoiceDto['status']>('WAITING_FOR_PAYMENT');

  public readonly supportUrl = computed(() => {
    const username = this._projectSettingsService.settings()?.supportUsername ?? '';
    const normalized = username.startsWith('@') ? username.slice(1) : username;
    return normalized ? `https://telegram.me/${normalized}` : null;
  });

  readonly choosePackage = computed(() => {
    const tier = this.data;
    const planTemplate = this.planTemplates().find((p) => p.type === tier.type);
    return planTemplate;
  });
  
  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    console.log(this.data);
    this._planTemplatesService.startPlanTemplatesSync();
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.close({ action: 'close', source: 'backdrop' });
    });
    this.dialogRef.keydownEvents().pipe(takeUntil(this._destroy$)).subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close({ action: 'close', source: 'esc' });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onContinueToInvoice(selectedBilling: BillingType): void {
    const packageType = this.choosePackage()?.type;
    const currentPackageType = this.authService.accountDto()?.userPlan?.userPlanType;
    let billingType = '';
    switch (selectedBilling) {
      case 'monthly':
        billingType = 'MONTHLY';
        break;
      case 'annual':
        billingType = 'YEARLY';
        break;
    }

    switch (currentPackageType) {
      case 'BASIC':
        this._apiService.postUserPlanPurchase({type:packageType as UserPlanType, billingCycle: billingType as 'MONTHLY' | 'YEARLY' | 'CUSTOM'}).pipe(take(1)).subscribe({
          next: (response: {invoiceUuid: string}) => {
            this.invoiceUuid.set(response.invoiceUuid);
            this.goToStage('invoice_state');
          },
          error: (error) => this._handleInvoiceError('postUserPlanPurchase', error),
        });
        break;
      default:
        this._apiService.postUserPlanUpgrade({type:packageType as UserPlanType, billingCycle: billingType as 'MONTHLY' | 'YEARLY' | 'CUSTOM'}).pipe(take(1)).subscribe({
          next: (response: {invoiceUuid: string}) => {
            this.invoiceUuid.set(response.invoiceUuid);
            this.goToStage('invoice_state');
          },
          error: (error) => this._handleInvoiceError('postUserPlanUpgrade', error),
        });
        break;
    }
  }

  private _handleInvoiceError(source: string, error: any): void {
    console.error(source, error);
    if (error?.error?.code === ERROR_CODE_PLAN_INVOICE_UNAVAILABLE) {
      this.goToStage('support_error_state');
    }
  }

  public goToStage(stage: PurchaseStage): void {
    this.stage.set(stage);
  }

  public close(payload: RenewalDialogClosePayload = { action: 'close', source: 'button' }): void {
    if (payload.action !== 'dismiss' && this.stage() === 'invoice_state') {
      if (this.invoiceStatus() === 'WAITING_FOR_PAYMENT') {
        this.dialogRef.close({action: 'toast', source: 'button'});
      } else {
        this.dialogRef.close(payload);
      }
    } else {
      this.dialogRef.close(payload);
    }
    this.variablesService.triggerBannersReload();
  }
}
